import type { AutomationType } from '@playbook-technologies/common-enums';
import { useState } from 'react';

import type { Async } from '@client/new/helpers/types';

import { AUTOMATION_COPY, AutomationConfigField } from '../automation-copy';
import {
  CreatorAutomationConfig,
  UpdateAutomationConfigRequest,
} from '../automations.api';
import {
  AutomationsLoadError,
  AutomationsModel,
  AutomationsUpdateError,
  AutomationsUpdateStatus,
  useAutomationsModel,
} from '../model/Automations.model';

export const ID_FORMAT_MESSAGE = 'Must be a positive integer id';

/** Text fields the admin edits before an explicit save. Ids stay strings so
 *  a half-typed value is representable; empty clears the key on save. */
export type AutomationConfigDraft = {
  firstWorkoutId: string;
  otpPitchTargetProductId: string;
};

export type AutomationConfigValidation = Partial<
  Record<keyof AutomationConfigDraft, string>
>;

export type AutomationConfigState = {
  fields: readonly AutomationConfigField[];
  draft: AutomationConfigDraft;
  validation: AutomationConfigValidation;
  includePushes: boolean;
  dirty: boolean;
  canSave: boolean;
};

/**
 * The server checks monthly_to_annual readiness (prices, price path) only
 * when enabling, so before the first attempt the row can only say the
 * check is pending. `missing` carries the server's reason verbatim.
 */
export type AutomationReadiness =
  | { kind: 'ready' }
  | { kind: 'missing'; message: string }
  | { kind: 'unchecked' };

export type AutomationRow = {
  automationType: AutomationType;
  label: string;
  description: string;
  enabled: boolean;
  pending: boolean;
  error?: string;
  needsSetup: boolean;
  retry?: () => Promise<void>;
  status?: string;
  config: AutomationConfigState;
  readiness?: AutomationReadiness;
};

export type AutomationsViewModel = {
  rows: AutomationRow[];
  retry: AutomationsModel['retry'];
  update: (automationType: AutomationType, enabled: boolean) => Promise<void>;
  setConfigDraft: (
    automationType: AutomationType,
    patch: Partial<AutomationConfigDraft>,
  ) => void;
  saveConfig: (automationType: AutomationType) => Promise<void>;
  setIncludePushes: (
    automationType: AutomationType,
    includePushes: boolean,
  ) => Promise<void>;
};

type AutomationsViewModelError = {
  message: string;
  retry: () => Promise<void>;
};

const LOAD_ERROR_MESSAGES: Record<AutomationsLoadError['kind'], string> = {
  unauthorized:
    'Your session has expired. Sign in again to manage automations.',
  forbidden: "You don't have permission to manage automations.",
  network: "We couldn't load automations.",
  contract: 'No automation types are available.',
};

const UPDATE_ERROR_MESSAGES: Record<
  Exclude<AutomationsUpdateError['kind'], 'rejected'>,
  string
> = {
  unauthorized:
    'Your session has expired. Your previous setting has been restored.',
  forbidden:
    "You don't have permission to change this automation. Your previous setting has been restored.",
  network:
    "We couldn't update this automation. Your previous setting has been restored. Try again.",
};

/** Config saves are not optimistic, so nothing is rolled back on failure. */
const CONFIG_ERROR_MESSAGES: Record<
  Exclude<AutomationsUpdateError['kind'], 'rejected'>,
  string
> = {
  unauthorized:
    'Your session has expired. The configuration was not saved. Sign in again.',
  forbidden:
    "You don't have permission to change this automation. The configuration was not saved.",
  network: "We couldn't save this configuration. Try again.",
};

const statusMessage = (
  status: AutomationsUpdateStatus | undefined,
  label: string,
): string | undefined => {
  if (!status) return undefined;
  return status.kind === 'saving' ? `Saving ${label}` : `${label} saved`;
};

const errorMessage = (failure: AutomationsUpdateError): string => {
  if (failure.kind === 'rejected') return failure.message;
  return failure.intendedEnabled === undefined
    ? CONFIG_ERROR_MESSAGES[failure.kind]
    : UPDATE_ERROR_MESSAGES[failure.kind];
};

const toSavedDraft = (
  config: CreatorAutomationConfig | null,
): AutomationConfigDraft => ({
  firstWorkoutId: config?.firstWorkout
    ? String(config.firstWorkout.workoutId)
    : '',
  otpPitchTargetProductId: config?.otpPitchTargetProductId ?? '',
});

const isValidId = (raw: string): boolean =>
  raw === '' || /^[1-9]\d*$/.test(raw);

export const validateConfigDraft = (
  draft: AutomationConfigDraft,
  fields: readonly AutomationConfigField[],
): AutomationConfigValidation => {
  const validation: AutomationConfigValidation = {};
  if (fields.includes('firstWorkout') && !isValidId(draft.firstWorkoutId)) {
    validation.firstWorkoutId = ID_FORMAT_MESSAGE;
  }
  if (
    fields.includes('otpPitchTargetProductId') &&
    !isValidId(draft.otpPitchTargetProductId)
  ) {
    validation.otpPitchTargetProductId = ID_FORMAT_MESSAGE;
  }
  return validation;
};

/** Only changed keys go on the wire: re-sending firstWorkout would make the
 *  server mint a fresh deep link for an unchanged workout. */
export const toConfigRequest = (
  draft: AutomationConfigDraft,
  saved: AutomationConfigDraft,
  fields: readonly AutomationConfigField[],
): UpdateAutomationConfigRequest => {
  const request: UpdateAutomationConfigRequest = {};
  if (
    fields.includes('firstWorkout') &&
    draft.firstWorkoutId !== saved.firstWorkoutId
  ) {
    request.firstWorkout =
      draft.firstWorkoutId === ''
        ? null
        : { kind: 'workout', workoutId: Number(draft.firstWorkoutId) };
  }
  if (
    fields.includes('otpPitchTargetProductId') &&
    draft.otpPitchTargetProductId !== saved.otpPitchTargetProductId
  ) {
    request.otpPitchTargetProductId =
      draft.otpPitchTargetProductId === ''
        ? null
        : draft.otpPitchTargetProductId;
  }
  return request;
};

export const toReadiness = (
  enabled: boolean,
  failure: AutomationsUpdateError | undefined,
): AutomationReadiness => {
  if (enabled) return { kind: 'ready' };
  if (failure?.kind === 'rejected' && failure.onEnable) {
    return { kind: 'missing', message: failure.message };
  }
  return { kind: 'unchecked' };
};

type Drafts = Partial<Record<AutomationType, Partial<AutomationConfigDraft>>>;

export const useAutomationsViewModel = (
  creatorId: number,
): Async<AutomationsViewModel, AutomationsViewModelError> => {
  const model = useAutomationsModel(creatorId);
  const [drafts, setDrafts] = useState<Drafts>({});

  if (model.type === 'fetch-error') {
    return {
      type: 'fetch-error',
      value: {
        message: LOAD_ERROR_MESSAGES[model.value.kind],
        retry: model.value.retry,
      },
    };
  }
  if (model.type !== 'fetched') return model;

  const rows = model.value.automations.map((automation): AutomationRow => {
    const { automationType } = automation;
    const failure = model.value.errors[automationType];
    const { label, description, configFields, serverReadinessCheck } =
      AUTOMATION_COPY[automationType];
    const pending = model.value.pending.has(automationType);
    const saved = toSavedDraft(automation.config);
    const draft = { ...saved, ...drafts[automationType] };
    const validation = validateConfigDraft(draft, configFields);
    const dirty =
      Object.keys(toConfigRequest(draft, saved, configFields)).length > 0;
    const intendedEnabled =
      failure && failure.kind !== 'rejected'
        ? failure.intendedEnabled
        : undefined;

    return {
      automationType,
      label,
      description,
      enabled: automation.enabled,
      pending,
      error: failure ? errorMessage(failure) : undefined,
      needsSetup: failure?.kind === 'rejected' && failure.onEnable,
      retry:
        intendedEnabled !== undefined
          ? () => model.value.update(automationType, intendedEnabled)
          : undefined,
      status: statusMessage(model.value.statuses[automationType], label),
      config: {
        fields: configFields,
        draft,
        validation,
        includePushes: automation.config?.includePushes ?? true,
        dirty,
        canSave: dirty && Object.keys(validation).length === 0 && !pending,
      },
      readiness: serverReadinessCheck
        ? toReadiness(automation.enabled, failure)
        : undefined,
    };
  });

  return {
    type: 'fetched',
    value: {
      rows,
      retry: model.value.retry,
      /** Enabling with an unsaved valid config draft sends both in one PATCH,
       *  so the server validates the config the admin sees in the fields. */
      update: (automationType, enabled) => {
        const row = rows.find((r) => r.automationType === automationType);
        const config =
          enabled &&
          row &&
          row.config.dirty &&
          Object.keys(row.config.validation).length === 0
            ? toConfigRequest(
                row.config.draft,
                toSavedDraft(
                  model.value.automations.find(
                    (item) => item.automationType === automationType,
                  )?.config ?? null,
                ),
                row.config.fields,
              )
            : undefined;
        return model.value.update(automationType, enabled, config);
      },
      setConfigDraft: (automationType, patch) =>
        setDrafts((current) => ({
          ...current,
          [automationType]: { ...current[automationType], ...patch },
        })),
      saveConfig: async (automationType) => {
        const row = rows.find((r) => r.automationType === automationType);
        if (!row || !row.config.canSave) return;
        const automation = model.value.automations.find(
          (item) => item.automationType === automationType,
        );
        await model.value.saveConfig(
          automationType,
          toConfigRequest(
            row.config.draft,
            toSavedDraft(automation?.config ?? null),
            row.config.fields,
          ),
        );
      },
      setIncludePushes: (automationType, includePushes) =>
        model.value.saveConfig(automationType, { includePushes }),
    },
  };
};
